import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { attachIntervalToDisplayObject, CountDownFormat, formatIntoUnitCountdown } from '../../../utils/time-utils';
import {
  getChestToOpenIndex,
  getCurrentChestProgressVisual,
  getWinStreakEndTime,
  getWinStreakMinLevel,
} from '../WinStreakUtils';
import { WinStreakBaseIconView } from './WinStreakBaseIconView';
import type { WinStreakState } from '../WinStreakState';
import type { MenuIcon } from '../../../mainMenu/types';
import { LevelLockMenuIconOverlay } from '../../../views/LevelLockMenuIconOverlay';
import type { WinStreakObservable } from '../WinStreakController';

export class WinStreakMenuIconView extends Container implements MenuIcon {
  public readonly onVisibilityChanged = new Signal<(visible: boolean) => void>();

  public readonly onClicked = new Signal();

  private readonly iconView: WinStreakBaseIconView = new WinStreakBaseIconView().showTimer().props({
    parent: this,
  });

  private cancelTimeUpdate: (() => void) | null = null;

  constructor(private readonly observable: WinStreakObservable) {
    super();
    if (getWinStreakMinLevel() > observable.getValue().levelIndex + 1) {
      this.initLocked();
    } else {
      this.init();
    }
  }

  private init(): void {
    this.iconView.eventMode = 'static';

    this.iconView.on('pointertap', () => {
      this.onClicked.emit();
    });

    this.observable.addChangeHandler(this, () => {
      this.refreshIconState();
    });
  }

  private initLocked(): void {
    new LevelLockMenuIconOverlay(getWinStreakMinLevel()).props({
      parent: this,
    });
    this.iconView.hideTimer();
    this.iconView.setNotification(null);
  }

  private refreshIconState(): void {
    const winStreakState = this.observable.getValue().winStreakActiveEvent;

    const shouldBeVisible = !!(winStreakState && !winStreakState.finished);

    this.props({
      visible: shouldBeVisible,
    });
    this.onVisibilityChanged.emit(shouldBeVisible);

    if (!winStreakState || winStreakState.finished) {
      this?.cancelTimeUpdate?.();
      return;
    }

    this.refreshNotification(winStreakState);

    const gotChestToOpen = getChestToOpenIndex(winStreakState) !== null;
    this.iconView.setProgress(
      winStreakState.currentProgress,
      gotChestToOpen ? getCurrentChestProgressVisual(winStreakState) : 1,
    );

    this?.cancelTimeUpdate?.();

    this.cancelTimeUpdate = attachIntervalToDisplayObject(
      this.iconView,
      () => {
        const timeLeft = getWinStreakEndTime(winStreakState) - Date.now();
        this.iconView.showTimer();
        this.iconView.updateTimerText(formatIntoUnitCountdown(timeLeft, 2, CountDownFormat.DHM));
        if (timeLeft <= 0) this.iconView.hideTimer();
      },
      1000,
    );
  }

  private refreshNotification(winStreakState: WinStreakState): void {
    const { currentProgress, lastSeenProgress } = winStreakState;
    const diff = currentProgress - (lastSeenProgress ?? 0);

    if (diff === 0) {
      this.iconView.setNotification(null);
    } else {
      this.iconView.setNotification(diff > 0 ? `+${diff}` : '!');
    }
  }

  public isVisible(): boolean {
    return this.visible;
  }
}
